/**
 * Shared layout for /admin/* pages. Renders a top bar with three nav
 * tabs (Tenants / Invitation codes / Audit log) plus the signed-in
 * staff email. Pages render inside `<main>` underneath.
 *
 * Why a separate shell from AppShell: Xamun staff are platform-level
 * operators, not tenants — they don't have a "current workspace" the
 * way customer CXOs do, so the workspace switcher, persona home, and
 * right-rail co-pilot all noise up the admin view. This shell keeps
 * the chrome focused on the platform job.
 */

import { Link, NavLink, useLocation } from "react-router-dom";
import { useMe } from "../../hooks/useMe";

const TABS: Array<{ to: string; label: string }> = [
  { to: "/admin/tenants",          label: "Tenants" },
  { to: "/admin/invitation-codes", label: "Invitation codes" },
  { to: "/admin/access-requests",  label: "Access requests" },
  { to: "/admin/audit",            label: "Audit log" },
];

export function AdminShell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const { pathname } = useLocation();
  // `/admin/tenants/:id` should keep the Tenants tab highlighted.
  const activeTabPath = (() => {
    for (const t of TABS) {
      if (pathname === t.to || pathname.startsWith(`${t.to}/`)) return t.to;
    }
    return "";
  })();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-canvas, #f8fafc)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "12px 24px",
          background: "var(--bg, #fff)",
          borderBottom: "1px solid var(--border, #e2e8f0)",
        }}
      >
        <Link
          to="/admin/tenants"
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: "var(--text, #0f172a)",
            textDecoration: "none",
            letterSpacing: -0.01,
          }}
        >
          PlayStudio · Admin
        </Link>
        <nav style={{ display: "flex", gap: 4 }}>
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              style={{
                fontSize: 13,
                padding: "6px 12px",
                borderRadius: 6,
                textDecoration: "none",
                color:
                  activeTabPath === t.to
                    ? "var(--accent, #6366f1)"
                    : "var(--text-2, #475569)",
                background:
                  activeTabPath === t.to
                    ? "var(--accent-soft, rgba(99,102,241,0.10))"
                    : "transparent",
                fontWeight: activeTabPath === t.to ? 600 : 500,
              }}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 12,
            color: "var(--text-3, #94a3b8)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {me.data?.user.email ?? ""}
          <Link
            to="/"
            style={{
              fontSize: 12,
              color: "var(--text-3, #94a3b8)",
              textDecoration: "underline",
            }}
          >
            ← exit
          </Link>
        </span>
      </header>
      <main style={{ flex: 1 }}>{children}</main>
    </div>
  );
}
